import type { SessionResult } from "@src/types/session"
import { assign, fromPromise, setup } from "xstate"

export const SESSION_MAX_ATTEMPTS = 5
export const SESSION_RETRY_BASE_MS = 5_000
export const SESSION_RETRY_MAX_MS = 30_000

export interface AppSessionContext {
  address: string
  chainType: string
  attemptCount: number
  failureReason: string | null
  sessionResult: SessionResult | null
}

export type AppSessionInput = {
  address: string
  chainType: string
}

export function sessionRetryDelayMs(attemptCount: number): number {
  return Math.min(
    SESSION_RETRY_BASE_MS * 2 ** Math.max(0, attemptCount - 1),
    SESSION_RETRY_MAX_MS
  )
}

/**
 * Session validation machine.
 *
 * Boundaries:
 * - Input: wallet identity (`address`, `chainType`).
 * - Output: terminal `SessionResult`.
 * - Internal retry loop: `checking -> retrying -> checking` (capped).
 *
 * Side effects (`checkSession`) are injected via `.provide()`.
 */
export const appSessionMachine = setup({
  types: {} as {
    context: AppSessionContext
    input: AppSessionInput
    output: SessionResult
  },
  actors: {
    checkSession: fromPromise(
      async (_: {
        input: { address: string; chainType: string }
      }): Promise<SessionResult> => {
        throw new Error("checkSession must be provided via .provide()")
      }
    ),
  },
  guards: {
    isSessionResultRetryable: (_, params: { result: SessionResult }) =>
      params.result.status === "retryable_failure",
    canRetryAfterFailure: ({ context }) =>
      context.attemptCount + 1 < SESSION_MAX_ATTEMPTS,
    shouldRetryRetryableResult: (
      { context },
      params: { result: SessionResult }
    ) =>
      params.result.status === "retryable_failure" &&
      context.attemptCount + 1 < SESSION_MAX_ATTEMPTS,
  },
  actions: {
    recordRetryableFailure: assign({
      attemptCount: ({ context }) => context.attemptCount + 1,
      sessionResult: ({ event }) => event.output,
      failureReason: ({ event }) => event.output.reason,
    }),
    recordInvokeError: assign({
      attemptCount: ({ context }) => context.attemptCount + 1,
      failureReason: ({ event }) =>
        event.error instanceof Error
          ? event.error.message
          : "Session check failed",
    }),
    finalizeExhaustedFailure: assign({
      sessionResult: ({ context }): SessionResult => ({
        status: "retryable_failure",
        reason: context.failureReason ?? "Session check failed",
      }),
    }),
  },
  delays: {
    SESSION_RETRY_DELAY: ({ context }) =>
      sessionRetryDelayMs(context.attemptCount),
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QEMAOqC0s6wJYHsA7AOgGMALMUga10KgGIIixi6A3fa1tTbWPETKUadKAg75SyAC4FCAbQAMAXWUrEoVPjxyimkAA9EAJgAcAZmImA7AFY7SiwEYlANgAsHgJw2ANCAAnqZKZsR2bt4mnnbOzh42Sl4AvskBvFg48sJUtPRMLGyEnNzEGfyCJBS5YhLFUrLy6grOGkgg2rryBsYIXkrEzt4WXrFuzo4ebgHBCBYmdsS+CQtJFqHezjap6eiZAtnVovlgAE6n+KfEqAA2sgBmlwC2ZXsVhyJ54pLSeoqq6gMnVwfx6pjMzmsdhMHiUSmiblCkRmiFcHiWNjcdkSnjMm0cFh2IHKWSEpzAMlOgTEDEMsBksh49xkZwAFAAlACiABV2QBNAD6AGVOUKhQBJADyADlhQAJSXs7kASgYJIOZIpVLEgPawNB7V6Hmhg0icMxSm8bhcdhRCGcbiWcPMowW3liNkJRMI+AgcAM6sqQJ0IO6hsQGGmQQjiXC3l8CzsLjRWKJgY+NXowa6+nDfRMdtcjrc0WxVtCZmxMLTb1JJHJlOpWb1IYNoF6sUWnrsHhMThicO8hacxASWOt9jMlqn2zSxNrGpIzEIYGzodz7cQbhs3iWbjMNjxxu8xp8w5M1lLB7MlYsdgh3lSqSAA */
  id: "app-session",
  context: ({ input }) => ({
    address: input.address,
    chainType: input.chainType,
    attemptCount: 0,
    failureReason: null,
    sessionResult: null,
  }),
  output: ({ context }) => {
    const r = context.sessionResult
    if (r == null) {
      throw new Error(
        "appSessionMachine: missing sessionResult for machine output"
      )
    }
    return r
  },
  initial: "checking",
  states: {
    checking: {
      invoke: {
        src: "checkSession",
        input: ({ context }) => ({
          address: context.address,
          chainType: context.chainType,
        }),
        onDone: [
          {
            guard: {
              type: "shouldRetryRetryableResult",
              params: ({ event }) => ({ result: event.output }),
            },
            target: "retrying",
            actions: "recordRetryableFailure",
          },
          {
            guard: {
              type: "isSessionResultRetryable",
              params: ({ event }) => ({ result: event.output }),
            },
            target: "done",
            actions: "recordRetryableFailure",
          },
          {
            target: "done",
            actions: assign({
              sessionResult: ({ event }) => event.output,
            }),
          },
        ],
        onError: [
          {
            guard: "canRetryAfterFailure",
            target: "retrying",
            actions: "recordInvokeError",
          },
          {
            target: "done",
            actions: ["recordInvokeError", "finalizeExhaustedFailure"],
          },
        ],
      },
    },
    retrying: {
      after: {
        SESSION_RETRY_DELAY: {
          target: "checking",
          reenter: true,
        },
      },
    },
    done: {
      type: "final",
    },
  },
})
